import type { ReactNode } from 'react';
import type { MantineNumberSize, MantineTheme } from '@mantine/core';
import { Modal, Stack, Text, Group, Title, Box } from '@mantine/core';
import { ActionButton } from './ActionButton';

export type ConfirmationModalSeverity = 'danger' | 'info';

interface ConfirmationModalProps {
  opened: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  title: string;
  /** Optional icon shown before the title (e.g. Tabler icon at 20–24px). */
  titleIcon?: ReactNode;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  confirmAriaLabel?: string;
  cancelAriaLabel?: string;
  onClose?: () => void;
  /** danger: red title (default). info: default body text color for the title. */
  severity?: ConfirmationModalSeverity;
  titleOrder?: 3 | 4;
  radius?: MantineNumberSize;
  /** apart: cancel left, confirm right. right: both actions grouped on the right (default). */
  actionsPosition?: 'apart' | 'right';
}

export function ConfirmationModal({
  opened,
  onConfirm,
  onCancel,
  title,
  titleIcon,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmAriaLabel,
  cancelAriaLabel,
  onClose,
  severity = 'danger',
  titleOrder = 3,
  radius,
  actionsPosition = 'right'
}: Readonly<ConfirmationModalProps>) {
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      handleClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const titleSx = (theme: MantineTheme) => ({
    color:
      severity === 'danger'
        ? theme.colorScheme === 'dark'
          ? theme.colors.red[4]
          : theme.colors.red[7]
        : theme.colorScheme === 'dark'
          ? theme.colors.dark[0]
          : theme.colors.gray[9],
    marginBottom: theme.spacing.sm
  });

  const modalTitle =
    titleIcon != null ? (
      <Group spacing="xs" align="center" noWrap sx={titleSx}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            lineHeight: 1
          }}
        >
          {titleIcon}
        </Box>
        <Title
          order={titleOrder}
          sx={{
            flex: 1,
            margin: 0,
            color: 'inherit',
            whiteSpace: 'nowrap'
          }}
        >
          {title}
        </Title>
      </Group>
    ) : (
      <Title order={titleOrder} sx={(theme) => ({ ...titleSx(theme), whiteSpace: 'nowrap' })}>
        {title}
      </Title>
    );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeButtonProps={{ tabIndex: -1 }}
      closeOnClickOutside={true}
      radius={radius}
      padding="lg"
      title={modalTitle}
      centered
      role="dialog"
      aria-label={title}
    >
      <Stack spacing="xl">
        <Text size="sm" sx={{ lineHeight: 1.6 }}>
          {message}
        </Text>
        <Group position={actionsPosition} spacing="sm">
          <ActionButton
            onClick={handleCancel}
            label={cancelLabel}
            ariaLabel={cancelAriaLabel ?? cancelLabel}
            actionType="secondary"
          />
          <ActionButton
            onClick={handleConfirm}
            label={confirmLabel}
            ariaLabel={confirmAriaLabel ?? confirmLabel}
            actionType="primary"
          />
        </Group>
      </Stack>
    </Modal>
  );
} 